import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Upload, Search, Calendar, FileText, ArrowLeft } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useDocuments } from "@/hooks/useDocuments";
import { useDocumentFolders } from "@/hooks/useDocumentFolders";
import { DocumentFolderTiles } from "@/components/ui/document-folder-tiles";
import { DocumentList } from "@/components/ui/document-list";
import { DocumentUpload } from "@/components/ui/document-upload";
import { useQuery } from "@tanstack/react-query";

// Mounted only after a folder is opened: the server rejects employee requests
// for client documents without a shared folderId.
function CompanyFolderDocuments({
  clientId,
  folderId,
}: {
  clientId: number;
  folderId: number;
}) {
  const { documents, loading, error } = useDocuments({
    ownerType: "client",
    clientId,
    folderId,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800 text-sm">Error loading documents: {error}</p>
      </div>
    );
  }
  // readOnly: employees can view/download company docs but not edit/delete
  return <DocumentList documents={documents as any} readOnly />;
}

export default function Documents() {
  const { toast } = useToast();
  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/user"] });
  const clientId = user?.clientId as number | undefined;
  const employeeId = user?.employeeId as number | undefined;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const { folders: companyFolders } = useDocumentFolders({
    ownerType: "client",
    clientId,
  });
  const [openCompanyFolder, setOpenCompanyFolder] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // Documents scoped to the employee
  const { documents, loading, error, refreshDocuments } = useDocuments({
    autoRefresh: true,
    ownerType: "employee",
    clientId,
    employeeId,
  });

  // Define category options for upload, edit dialogs, and filter buttons
  const categoryOptions = [
    { value: "hr", label: "HR" },
    { value: "finance", label: "Finance" },
    { value: "legal", label: "Legal" },
    { value: "policy", label: "Policy" },
    { value: "benefits", label: "Benefits" },
    { value: "performance", label: "Performance" },
    { value: "training", label: "Training" },
    { value: "payroll", label: "Payroll" },
    { value: "tax", label: "Tax" },
    { value: "compensation", label: "Compensation" },
    { value: "personal", label: "Personal / ID" },
    { value: "other", label: "Other" },
  ];
  const categories = ["All", ...categoryOptions.map((c) => c.value)];

  // Filter documents based on search and category
  const filteredDocuments = documents.filter((doc: any) => {
    const matchesSearch = doc.originalFileName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "All" ||
      doc.category?.toLowerCase() === selectedCategory.toLowerCase();
    return matchesSearch && matchesCategory;
  });

  // Document stats
  const totalDocuments = documents.length;
  const currentDocuments = documents.filter((doc: any) => doc.status === "current").length;
  const completedDocuments = documents.filter((doc: any) => doc.status === "completed").length;
  const onFileDocuments = documents.filter((doc: any) => doc.status === "on_file").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">My Documents</h1>
        <DocumentUpload
          trigger={
            <Button size="sm">
              <Upload className="h-4 w-4 mr-2" />
              Upload Document
            </Button>
          }
          ownerType="employee"
          clientId={clientId}
          employeeId={employeeId}
          categoryOptions={categoryOptions}
          onUploadSuccess={() => {
            refreshDocuments();
            toast({
              title: "Document uploaded",
              description: "Your document has been uploaded successfully.",
            });
          }}
        />
      </div>

      {/* Document Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Documents</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalDocuments}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Current</CardTitle>
            <Calendar className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {currentDocuments}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <FileText className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {completedDocuments}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">On File</CardTitle>
            <FileText className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {onFileDocuments}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search documents..." 
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={category === selectedCategory ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                >
                  {category === "All" ? "All" : category.charAt(0).toUpperCase() + category.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800 text-sm">
            Error loading documents: {error}
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <DocumentList
          documents={filteredDocuments as any}
          onDocumentUpdate={refreshDocuments}
          showActions={true}
          categoryOptions={categoryOptions}
        />
      )}

      {clientId && companyFolders.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            {openCompanyFolder && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpenCompanyFolder(null)}
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> All folders
              </Button>
            )}
            <h2 className="text-xl font-semibold">
              Company Documents
              {openCompanyFolder ? ` / ${openCompanyFolder.name}` : ""}
            </h2>
          </div>
          {openCompanyFolder ? (
            <CompanyFolderDocuments
              clientId={clientId}
              folderId={openCompanyFolder.id}
            />
          ) : (
            <DocumentFolderTiles
              folders={companyFolders.map((f) => ({
                ...f,
                documentCount: f.docCount,
                visibleToEmployees: false, // suppress shared badge — every folder here is shared
              }))}
              onOpenFolder={(f) =>
                setOpenCompanyFolder({ id: f.id, name: f.name })
              }
            />
          )}
        </div>
      )}

      {/* Upload Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Document Upload Guidelines</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-3">Accepted File Types</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• PDF documents (.pdf)</li>
                <li>• Word documents (.docx, .doc)</li>
                <li>• Images (.jpg, .png, .gif)</li>
                <li>• Spreadsheets (.xlsx, .xls)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-3">Upload Requirements</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Maximum file size: 10 MB</li>
                <li>• Files must be work-related</li>
                <li>• No personal or confidential content</li>
                <li>• Use descriptive file names</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}